import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Table, Pagination, Badge, Modal } from '@/components/ui';
import { UserRoleBadge } from '@/components/admin';
import type { Column, SortDirection } from '@/components/ui';
import { adminService } from '@/services/admin.service';
import type { AdminUser, AdminUserFilters } from '@/services/admin.service';
import type { UserRole } from '@/types';

const ROLES: { value: UserRole | ''; label: string }[] = [
  { value: '', label: 'All Roles' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'BROKER', label: 'Broker' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'STAFF', label: 'Staff' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
  const [tenantId, setTenantId] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Selection for bulk actions
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Modal state
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: AdminUserFilters = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortColumn,
        sortOrder: sortDirection || undefined,
      };

      if (search) filters.search = search;
      if (role) filters.role = role;
      if (status) filters.status = status;
      if (tenantId) filters.tenantId = tenantId;

      const response = await adminService.getUsers(filters);
      setUsers(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalItems(response.meta.total);
    } catch {
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, search, role, status, tenantId, sortColumn, sortDirection]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset selection when data changes
  useEffect(() => {
    setSelectedUsers(new Set());
    setIsAllSelected(false);
  }, [users]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUsers(new Set());
      setIsAllSelected(false);
    } else {
      setSelectedUsers(new Set(users.map((u) => u.id)));
      setIsAllSelected(true);
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
    setIsAllSelected(newSelected.size === users.length);
  };

  const handleDeactivate = async () => {
    if (!selectedUser) return;

    setIsProcessing(true);
    try {
      if (selectedUser.isActive) {
        await adminService.deactivateUser(selectedUser.id);
      } else {
        await adminService.activateUser(selectedUser.id);
      }
      setIsDeactivateModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch {
      setError('Failed to update user status');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    setIsProcessing(true);
    try {
      await adminService.resetPassword(selectedUser.id);
      setIsResetPasswordModalOpen(false);
      setSelectedUser(null);
    } catch {
      setError('Failed to send password reset email');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.size === 0) return;

    setIsProcessing(true);
    try {
      const promises = Array.from(selectedUsers).map((userId) =>
        bulkAction === 'activate'
          ? adminService.activateUser(userId)
          : adminService.deactivateUser(userId)
      );
      await Promise.all(promises);
      setIsBulkActionModalOpen(false);
      setBulkAction(null);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch {
      setError(`Failed to ${bulkAction} selected users`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const filters: AdminUserFilters = {};
      if (search) filters.search = search;
      if (role) filters.role = role;
      if (status) filters.status = status;
      if (tenantId) filters.tenantId = tenantId;

      const blob = await adminService.exportUsersToCSV(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError('Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  const openBulkActionModal = (action: 'activate' | 'deactivate') => {
    setBulkAction(action);
    setIsBulkActionModalOpen(true);
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={handleSelectAll}
          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
        />
      ),
      render: (user) => (
        <input
          type="checkbox"
          checked={selectedUsers.has(user.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectUser(user.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'name',
      header: 'User',
      sortable: true,
      render: (user) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={`${user.firstName} ${user.lastName}`}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-primary-700">
                {user.firstName.charAt(0)}
                {user.lastName.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <p className="font-medium text-secondary-900">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-secondary-500">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (user) => (
        <span className="text-secondary-600">{user.phone || '-'}</span>
      ),
    },
    {
      key: 'tenantName',
      header: 'Tenant',
      sortable: true,
      render: (user) => (
        <span className="text-secondary-700">{user.tenantName}</span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (user) => <UserRoleBadge role={user.role} />,
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (user) => (
        <Badge variant={user.isActive ? 'success' : 'danger'} dot>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'lastLoginAt',
      header: 'Last Login',
      sortable: true,
      render: (user) => (
        <span className="text-secondary-500">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      sortable: true,
      render: (user) => (
        <span className="text-secondary-500">
          {new Date(user.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/users/${user.id}`);
            }}
          >
            View
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(user);
              setIsResetPasswordModalOpen(true);
            }}
          >
            Reset Pwd
          </Button>
          <Button
            size="sm"
            variant={user.isActive ? 'danger' : 'primary'}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(user);
              setIsDeactivateModalOpen(true);
            }}
          >
            {user.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Users</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Manage all users across tenants ({totalItems} total)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={handleExportCSV}
            isLoading={isExporting}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <Card padding="sm" className="bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-700">
              {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-3">
              <Button
                size="sm"
                variant="primary"
                onClick={() => openBulkActionModal('activate')}
              >
                Activate Selected
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => openBulkActionModal('deactivate')}
              >
                Deactivate Selected
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedUsers(new Set());
                  setIsAllSelected(false);
                }}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card padding="sm">
        <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
            />
          </div>
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value as UserRole | '');
              setCurrentPage(1);
            }}
            className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {ROLES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as 'active' | 'inactive' | '');
              setCurrentPage(1);
            }}
            className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Input
            placeholder="Filter by Tenant ID..."
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="max-w-[200px]"
          />
          <Button type="submit">Search</Button>
          {(search || role || status || tenantId) && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setRole('');
                setStatus('');
                setTenantId('');
                setCurrentPage(1);
              }}
            >
              Clear
            </Button>
          )}
        </form>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-danger-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <Table
        columns={columns}
        data={users}
        keyExtractor={(user) => user.id}
        isLoading={isLoading}
        emptyMessage="No users found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(user) => navigate(`/users/${user.id}`)}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Deactivate/Activate Modal */}
      <Modal
        isOpen={isDeactivateModalOpen}
        onClose={() => {
          setIsDeactivateModalOpen(false);
          setSelectedUser(null);
        }}
        title={selectedUser?.isActive ? 'Deactivate User' : 'Activate User'}
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to {selectedUser?.isActive ? 'deactivate' : 'activate'}{' '}
            <span className="font-medium text-secondary-900">
              {selectedUser?.firstName} {selectedUser?.lastName}
            </span>
            ?
          </p>
          {selectedUser?.isActive && (
            <p className="mt-2 text-sm text-secondary-500">
              The user will no longer be able to access the platform.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeactivateModalOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={selectedUser?.isActive ? 'danger' : 'primary'}
            onClick={handleDeactivate}
            isLoading={isProcessing}
          >
            {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => {
          setIsResetPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        title="Reset Password"
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Send a password reset email to{' '}
            <span className="font-medium text-secondary-900">
              {selectedUser?.email}
            </span>
            ?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsResetPasswordModalOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleResetPassword} isLoading={isProcessing}>
            Send Reset Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        isOpen={isBulkActionModalOpen}
        onClose={() => {
          setIsBulkActionModalOpen(false);
          setBulkAction(null);
        }}
        title={bulkAction === 'activate' ? 'Activate Users' : 'Deactivate Users'}
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to {bulkAction}{' '}
            <span className="font-medium text-secondary-900">
              {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}
            </span>
            ?
          </p>
          {bulkAction === 'deactivate' && (
            <p className="mt-2 text-sm text-secondary-500">
              These users will no longer be able to access the platform.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsBulkActionModalOpen(false);
              setBulkAction(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={bulkAction === 'deactivate' ? 'danger' : 'primary'}
            onClick={handleBulkAction}
            isLoading={isProcessing}
          >
            {bulkAction === 'activate' ? 'Activate All' : 'Deactivate All'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserList;
